import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import PageContentEditor from "../../components/PageContentEditor";
import AnalyticsDashboard from "../../components/AnalyticsDashboard";
import PromotionsSection from "./PromotionsSection";
import BotInsightsSection from "./BotInsightsSection";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "../../components/ui/alert-dialog";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import {
  Users,
  Settings,
  CreditCard,
  Palette,
  Shield,
  Loader2,
  RefreshCw,
  DollarSign,
  UserCheck,
  AlertCircle,
  FileText,
  ExternalLink,
  Trash2,
  Search,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  Video,
  Download,
  Play,
  Share2,
  Copy,
  Check,
  Zap,
  Mail,
  Bot,
  MessageSquare,
  Lightbulb,
  Bug,
  AlertTriangle,
  Upload,
  ImageIcon,
  Crown,
  Plus as PlusIcon,
  Filter,
  X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";
const AdminDashboard = () => {
  const context = useOutletContext();
  const activeSection = context?.activeSection || "settings";
  const setActiveSection = context?.setActiveSection || (() => {});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    site_name: "AIQueryStudio",
    tagline: "Professional Database Management",
    primary_color: "#22c55e",
    accent_color: "#3b82f6",
    paywall_enabled: false,
    weekly_price_enabled: true,
    weekly_price: 0.35,
    subscription_price: 1.00,
    annual_price_enabled: true,
    annual_discount_percent: 15.0,
    // Company Contact Info
    company_name: "AIQueryStudio Inc.",
    company_website: "https://aiquerystudio.com",
    company_email: "support@aiquerystudio.com",
    stripe_publishable_key: "",
    stripe_secret_key: "",
    hero_title: "Manage Your Databases Like a Pro",
    hero_subtitle: "",
    features_title: "Everything You Need",
    features_subtitle: "",
    pricing_title: "Simple, Transparent Pricing",
    pricing_subtitle: "",
    cta_title: "Ready to Get Started?",
    cta_subtitle: ""
  });
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [renewalUsers, setRenewalUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionSummary, setTransactionSummary] = useState({ total_revenue: 0, paid_count: 0, pending_count: 0, total_count: 0 });
  const [passwordData, setPasswordData] = useState({ current: "", new: "", confirm: "" });
  const [editingUser, setEditingUser] = useState(null);
  const [stripeKeys, setStripeKeys] = useState({ publishable: "", secret: "" });
  
  // Logo state
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Payment filters
  const [paymentNameFilter, setPaymentNameFilter] = useState("");
  const [paymentDateFrom, setPaymentDateFrom] = useState("");
  const [paymentDateTo, setPaymentDateTo] = useState("");
  
  // Renewal filters
  const [renewalNameFilter, setRenewalNameFilter] = useState("");
  const [renewalDateFrom, setRenewalDateFrom] = useState("");
  const [renewalDateTo, setRenewalDateTo] = useState("");
  
  // Pagination state
  const [usersPage, setUsersPage] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [renewalsPage, setRenewalsPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  const getAuthHeaders = () => {
    const token = localStorage.getItem("admin_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers.Authorization) {
        toast.error("No admin token found. Please login again.");
        setLoading(false);
        return;
      }
      
      const [settingsRes, usersRes, renewalsRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/settings`, { headers }).catch(e => ({ data: {}, error: e })),
        axios.get(`${API_URL}/admin/users`, { headers }).catch(e => ({ data: [], error: e })),
        axios.get(`${API_URL}/admin/users/renewals`, { headers }).catch(e => ({ data: { users: [] }, error: e })),
        axios.get(`${API_URL}/admin/transactions`, { headers }).catch(e => ({ data: { transactions: [] }, error: e }))
      ]);
      
      // Check for errors
      if (settingsRes.error) {
        console.error("Settings fetch error:", settingsRes.error);
      }
      if (usersRes.error) {
        console.error("Users fetch error:", usersRes.error);
      }
      
      // Update settings
      const newSettings = {
        ...settings,
        ...settingsRes.data
      };
      setSettings(newSettings);
      
      // Update stripe keys from settings
      setStripeKeys({
        publishable: settingsRes.data?.stripe_publishable_key || "",
        secret: settingsRes.data?.stripe_secret_key || ""
      });
      
      // Load logo URL
      if (settingsRes.data?.logo_url) {
        setLogoUrl(settingsRes.data.logo_url);
      }
      
      // Handle users - backend returns array directly, not {users: [...]}
      const usersData = usersRes.data;
      if (Array.isArray(usersData)) {
        setUsers(usersData);
      } else if (usersData?.users && Array.isArray(usersData.users)) {
        setUsers(usersData.users);
      } else {
        console.warn("Unexpected users data format:", usersData);
        setUsers([]);
      }
      
      setRenewalUsers(renewalsRes.data?.users || []);
      setTransactions(transactionsRes.data?.transactions || []);
      setTransactionSummary(transactionsRes.data?.summary || { total_revenue: 0, paid_count: 0, pending_count: 0, total_count: 0 });
    } catch (error) {
      console.error("Admin data fetch error:", error);
      toast.error("Failed to load admin data: " + (error.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/settings`, settings, { headers: getAuthHeaders() });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveStripeKeys = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/stripe-settings`, {
        stripe_publishable_key: stripeKeys.publishable,
        stripe_secret_key: stripeKeys.secret
      }, { headers: getAuthHeaders() });
      toast.success("Stripe settings saved");
      // Refresh data to show saved keys
      await fetchData();
    } catch (error) {
      toast.error("Failed to save Stripe settings");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePassword = async () => {
    if (!passwordData.current) {
      toast.error("Please enter your current password");
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordData.new.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return;
    }
    if (passwordData.current === passwordData.new) {
      toast.error("New password must be different from current password");
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/change-password`, 
        { 
          current_password: passwordData.current,
          new_password: passwordData.new 
        }, 
        { headers: getAuthHeaders() }
      );
      toast.success("Password updated successfully");
      setPasswordData({ current: "", new: "", confirm: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update password");
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5MB)"); return; }
    
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await axios.post(`${API_URL}/admin/logo`, formData, {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" }
      });
      setLogoUrl(response.data.logo_url);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleDeleteLogo = async () => {
    try {
      await axios.delete(`${API_URL}/admin/logo`, { headers: getAuthHeaders() });
      setLogoUrl(null);
      toast.success("Logo deleted");
    } catch (error) {
      toast.error("Failed to delete logo");
    }
  };

  const handleUpdateUserSubscription = async (userId, data) => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/user/${userId}/subscription`, data, { headers: getAuthHeaders() });
      toast.success("User updated successfully");
      setEditingUser(null);
      await fetchData();
      // Switch to users tab after successful update
      setActiveTab("users");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    try {
      await axios.delete(`${API_URL}/admin/user/${userId}`, { headers: getAuthHeaders() });
      toast.success("User deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleToggleBypass = async (userId, currentValue) => {
    try {
      await axios.put(`${API_URL}/admin/user/${userId}/subscription`, 
        { bypass_paywall: !currentValue }, 
        { headers: getAuthHeaders() }
      );
      toast.success("User bypass updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update user");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  const getStripePaymentLink = (userEmail, amount) => {
    // Generate a Stripe payment link for manual payment
    return `https://buy.stripe.com/test_payment?email=${encodeURIComponent(userEmail)}&amount=${amount}`;
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Filter transactions based on name and date filters
  const filteredTransactions = transactions.filter(tx => {
    // Name/email filter
    if (paymentNameFilter.trim()) {
      const search = paymentNameFilter.toLowerCase();
      const matchesName = tx.user_name?.toLowerCase().includes(search);
      const matchesEmail = tx.user_email?.toLowerCase().includes(search);
      if (!matchesName && !matchesEmail) return false;
    }
    
    // Date from filter
    if (paymentDateFrom) {
      const txDate = new Date(tx.created_at);
      const fromDate = new Date(paymentDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (txDate < fromDate) return false;
    }
    
    // Date to filter
    if (paymentDateTo) {
      const txDate = new Date(tx.created_at);
      const toDate = new Date(paymentDateTo);
      toDate.setHours(23, 59, 59, 999);
      if (txDate > toDate) return false;
    }
    
    return true;
  });

  // Calculate filtered totals
  const filteredSummary = {
    total_revenue: filteredTransactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + (t.amount || 0), 0),
    paid_count: filteredTransactions.filter(t => t.payment_status === 'paid').length,
    pending_count: filteredTransactions.filter(t => t.payment_status === 'pending').length,
    total_count: filteredTransactions.length
  };

  // Filter users based on search
  const filteredUsers = users
    .filter(user => {
      if (!userSearch.trim()) return true;
      const search = userSearch.toLowerCase();
      return (
        user.email?.toLowerCase().includes(search) ||
        user.name?.toLowerCase().includes(search)
      );
    })
    .sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
      return dateB - dateA;
    });

  // Filter renewals based on name and date filters
  const filteredRenewals = renewalUsers.filter(user => {
    // Name/email filter
    if (renewalNameFilter.trim()) {
      const search = renewalNameFilter.toLowerCase();
      const matchesName = user.name?.toLowerCase().includes(search);
      const matchesEmail = user.email?.toLowerCase().includes(search);
      if (!matchesName && !matchesEmail) return false;
    }
    
    // Date from filter (on subscription_expires)
    if (renewalDateFrom) {
      const expiryDate = new Date(user.subscription_expires);
      const fromDate = new Date(renewalDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (expiryDate < fromDate) return false;
    }
    
    // Date to filter
    if (renewalDateTo) {
      const expiryDate = new Date(user.subscription_expires);
      const toDate = new Date(renewalDateTo);
      toDate.setHours(23, 59, 59, 999);
      if (expiryDate > toDate) return false;
    }
    
    return true;
  });

  // Pagination helper
  const paginate = (items, page) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return items.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  };

  const getTotalPages = (items) => Math.ceil(items.length / ITEMS_PER_PAGE);

  // Paginated data
  const paginatedUsers = paginate(filteredUsers, usersPage);
  const paginatedPayments = paginate(filteredTransactions, paymentsPage);
  const paginatedRenewals = paginate(filteredRenewals, renewalsPage);

  // Reset page when filters change
  const handlePaymentFilterChange = (setter) => (value) => {
    setter(value);
    setPaymentsPage(1);
  };

  const handleRenewalFilterChange = (setter) => (value) => {
    setter(value);
    setRenewalsPage(1);
  };

  const handleUserSearchChange = (value) => {
    setUserSearch(value);
    setUsersPage(1);
  };

  // Pagination component
  const PaginationControls = ({ currentPage, totalPages, onPageChange, totalItems, itemName }) => (
    <div className="flex items-center justify-between mt-4 pt-4 border-t">
      <p className="text-sm text-muted-foreground">
        Showing {Math.min((currentPage - 1) * ITEMS_PER_PAGE + 1, totalItems)} - {Math.min(currentPage * ITEMS_PER_PAGE, totalItems)} of {totalItems} {itemName}
      </p>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
        >
          <ChevronLeft className="w-4 h-4" />
          Previous
        </Button>
        <span className="text-sm px-2">
          Page {currentPage} of {totalPages || 1}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
        >
          Next
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold capitalize">{activeSection}</h1>
            <p className="text-muted-foreground">Manage your site settings and users</p>
          </div>
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="w-4 h-4" />
            Refresh
          </Button>
        </div>

        {/* Site Settings Section */}
        {activeSection === "settings" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>Configure your site name and tagline</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Site Name</Label>
                    <Input
                      value={settings.site_name}
                      onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                      data-testid="settings-site-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Tagline</Label>
                    <Input
                      value={settings.tagline}
                      onChange={(e) => setSettings({ ...settings, tagline: e.target.value })}
                      data-testid="settings-tagline"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Company Logo */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5" />
                  Company Logo
                </CardTitle>
                <CardDescription>Upload a logo to display across your site</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-6">
                  {/* Preview */}
                  <div className="w-24 h-24 rounded-lg border border-border bg-muted/30 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {logoUrl ? (
                      <img src={`${API_URL.replace('/api', '')}${logoUrl}`} alt="Logo" className="w-full h-full object-contain" data-testid="logo-preview" />
                    ) : (
                      <ImageIcon className="w-8 h-8 text-muted-foreground/40" />
                    )}
                  </div>
                  
                  <div className="flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleUploadLogo}
                          disabled={uploadingLogo}
                          data-testid="logo-upload-input"
                        />
                        <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border border-border bg-card hover:bg-muted transition-colors ${uploadingLogo ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                          {uploadingLogo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                          {uploadingLogo ? "Uploading..." : "Upload Logo"}
                        </div>
                      </label>
                      {logoUrl && (
                        <Button variant="outline" size="sm" onClick={handleDeleteLogo} className="gap-1 text-destructive hover:text-destructive" data-testid="delete-logo-btn">
                          <Trash2 className="w-4 h-4" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      PNG, JPG, SVG or WebP. Max 5MB. Used in navigation, login, and landing page.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Company Contact Info
                </CardTitle>
                <CardDescription>Contact details shown in support/contact sections</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      value={settings.company_name}
                      onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                      placeholder="Your Company Inc."
                      data-testid="settings-company-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Website URL</Label>
                    <Input
                      value={settings.company_website}
                      onChange={(e) => setSettings({ ...settings, company_website: e.target.value })}
                      placeholder="https://yourcompany.com"
                      data-testid="settings-company-website"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Support Email</Label>
                    <Input
                      type="email"
                      value={settings.company_email}
                      onChange={(e) => setSettings({ ...settings, company_email: e.target.value })}
                      placeholder="support@yourcompany.com"
                      data-testid="settings-company-email"
                    />
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  These details will be displayed in the Contact Us and Support sections of your website.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Credits & Pricing</CardTitle>
                <CardDescription>Configure the credit system and purchase packages</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Default Credits for New Users</Label>
                    <Input
                      type="number"
                      min="0"
                      value={settings.default_credits ?? 100}
                      onChange={(e) => setSettings({ ...settings, default_credits: parseInt(e.target.value) || 0 })}
                      data-testid="settings-default-credits"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Credit Cost Per Query</Label>
                    <Input
                      type="number"
                      min="1"
                      value={settings.credit_cost_per_query ?? 1}
                      onChange={(e) => setSettings({ ...settings, credit_cost_per_query: parseInt(e.target.value) || 1 })}
                      data-testid="settings-credit-cost"
                    />
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <Label className="mb-3 block">Credit Packages (editable)</Label>
                  <div className="space-y-2">
                    {(settings.credit_packages || []).map((pkg, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <div className="flex-1 grid grid-cols-3 gap-2">
                          <div>
                            <span className="text-xs text-muted-foreground">Credits</span>
                            <Input
                              type="number"
                              min="1"
                              value={pkg.credits}
                              onChange={(e) => {
                                const updated = [...(settings.credit_packages || [])];
                                updated[idx] = { ...updated[idx], credits: parseInt(e.target.value) || 0 };
                                setSettings({ ...settings, credit_packages: updated });
                              }}
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">Price ($)</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={pkg.price}
                              onChange={(e) => {
                                const updated = [...(settings.credit_packages || [])];
                                updated[idx] = { ...updated[idx], price: parseFloat(e.target.value) || 0 };
                                setSettings({ ...settings, credit_packages: updated });
                              }}
                              className="h-8 text-sm"
                            />
                          </div>
                          <div>
                            <span className="text-xs text-muted-foreground">Label</span>
                            <Input
                              value={pkg.label}
                              onChange={(e) => {
                                const updated = [...(settings.credit_packages || [])];
                                updated[idx] = { ...updated[idx], label: e.target.value };
                                setSettings({ ...settings, credit_packages: updated });
                              }}
                              className="h-8 text-sm"
                            />
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive h-8"
                          onClick={() => {
                            const updated = (settings.credit_packages || []).filter((_, i) => i !== idx);
                            setSettings({ ...settings, credit_packages: updated });
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      onClick={() => {
                        const updated = [...(settings.credit_packages || []), { credits: 100, price: 10, label: "100 Credits" }];
                        setSettings({ ...settings, credit_packages: updated });
                      }}
                    >
                      <PlusIcon className="w-4 h-4" />
                      Add Package
                    </Button>
                  </div>
                </div>

                <Button onClick={handleSaveSettings} disabled={saving} data-testid="save-settings-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Analytics Section */}
        {activeSection === "analytics" && (
          <AnalyticsDashboard getAuthHeaders={getAuthHeaders} />
        )}

        {/* CMS Section */}
        {activeSection === "cms" && (
          <div className="space-y-6">
            <PageContentEditor getAuthHeaders={getAuthHeaders} />
          </div>
        )}

        {/* Branding Section */}
        {activeSection === "branding" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Colors</CardTitle>
                <CardDescription>Customize your brand colors</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        value={settings.primary_color}
                        onChange={(e) => setSettings({ ...settings, primary_color: e.target.value })}
                        data-testid="settings-primary-color"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Accent Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={settings.accent_color}
                        onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                        className="w-16 h-10 p-1"
                      />
                      <Input
                        value={settings.accent_color}
                        onChange={(e) => setSettings({ ...settings, accent_color: e.target.value })}
                        data-testid="settings-accent-color"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="mt-6 p-6 rounded-lg border border-border bg-card/50">
                  <h4 className="text-sm font-medium mb-4">Preview</h4>
                  <div className="flex items-center gap-4">
                    <Button style={{ backgroundColor: settings.primary_color }}>
                      Primary Button
                    </Button>
                    <Button style={{ backgroundColor: settings.accent_color }}>
                      Accent Button
                    </Button>
                    <div 
                      className="px-4 py-2 rounded-lg text-sm"
                      style={{ backgroundColor: `${settings.primary_color}20`, color: settings.primary_color }}
                    >
                      Badge Example
                    </div>
                  </div>
                </div>

                <Button onClick={handleSaveSettings} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Branding
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Stripe Section */}
        {activeSection === "stripe" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Stripe Configuration</CardTitle>
                <CardDescription>Enter your Stripe API credentials</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Publishable Key</Label>
                  <Input
                    value={stripeKeys.publishable}
                    onChange={(e) => setStripeKeys({ ...stripeKeys, publishable: e.target.value })}
                    placeholder="pk_live_..."
                    data-testid="stripe-publishable-key"
                  />
                  <p className="text-xs text-muted-foreground">Starts with pk_live_ or pk_test_</p>
                </div>
                <div className="space-y-2">
                  <Label>Secret Key</Label>
                  <Input
                    type="password"
                    value={stripeKeys.secret}
                    onChange={(e) => setStripeKeys({ ...stripeKeys, secret: e.target.value })}
                    placeholder="sk_live_..."
                    data-testid="stripe-secret-key"
                  />
                  <p className="text-xs text-muted-foreground">Starts with sk_live_ or sk_test_</p>
                </div>
                
                <Button onClick={handleSaveStripeKeys} disabled={saving} data-testid="save-stripe-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Stripe Settings
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Payment Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20">
                    <div className="flex items-center gap-3">
                      <DollarSign className="w-8 h-8 text-green-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Total Revenue</p>
                        <p className="text-2xl font-bold">
                          ${transactions.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + t.amount, 0).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <CreditCard className="w-8 h-8 text-blue-500" />
                      <div>
                        <p className="text-sm text-muted-foreground">Transactions</p>
                        <p className="text-2xl font-bold">{transactions.length}</p>
                      </div>
                    </div>
                  </div>
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-3">
                      <UserCheck className="w-8 h-8 text-primary" />
                      <div>
                        <p className="text-sm text-muted-foreground">Active Subscribers</p>
                        <p className="text-2xl font-bold">
                          {users.filter(u => u.subscription_status === 'active').length}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Transaction History</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Auto Renew</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions.map(tx => (
                      <TableRow key={tx.id}>
                        <TableCell>{formatDate(tx.created_at)}</TableCell>
                        <TableCell>{tx.user_email}</TableCell>
                        <TableCell>${tx.amount.toFixed(2)}</TableCell>
                        <TableCell>
                          <Badge variant={tx.payment_status === 'paid' ? 'default' : 'secondary'}>
                            {tx.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>{tx.auto_renew ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                    {transactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground">
                          No transactions yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Payments Section */}
        {activeSection === "payments" && (
          <div className="space-y-6">
            {/* Active Filter Banner */}
            {paymentNameFilter && (
              <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary" />
                  <span className="text-sm">
                    Showing payments for: <strong>{paymentNameFilter}</strong>
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setPaymentNameFilter("");
                    setPaymentDateFrom("");
                    setPaymentDateTo("");
                    setPaymentsPage(1);
                  }}
                  className="gap-2"
                  data-testid="reset-payment-filter"
                >
                  <X className="w-4 h-4" />
                  Reset Filter
                </Button>
              </div>
            )}
            
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={paymentNameFilter}
                      onChange={(e) => handlePaymentFilterChange(setPaymentNameFilter)(e.target.value)}
                      className="pl-9"
                      data-testid="payment-name-filter"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <Input
                      type="date"
                      value={paymentDateFrom}
                      onChange={(e) => handlePaymentFilterChange(setPaymentDateFrom)(e.target.value)}
                      className="w-40"
                      data-testid="payment-date-from"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="date"
                      value={paymentDateTo}
                      onChange={(e) => handlePaymentFilterChange(setPaymentDateTo)(e.target.value)}
                      className="w-40"
                      data-testid="payment-date-to"
                    />
                  </div>
                  {(paymentNameFilter || paymentDateFrom || paymentDateTo) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setPaymentNameFilter("");
                        setPaymentDateFrom("");
                        setPaymentDateTo("");
                        setPaymentsPage(1);
                      }}
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
              <Card className="bg-green-500/10 border-green-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <DollarSign className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Total Revenue</p>
                      <p className="text-2xl font-bold text-green-500">
                        ${filteredSummary.total_revenue.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-blue-500/10 border-blue-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-8 h-8 text-blue-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Paid Transactions</p>
                      <p className="text-2xl font-bold text-blue-500">{filteredSummary.paid_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-yellow-500/10 border-yellow-500/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-8 h-8 text-yellow-500" />
                    <div>
                      <p className="text-sm text-muted-foreground">Pending</p>
                      <p className="text-2xl font-bold text-yellow-500">{filteredSummary.pending_count}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-primary/10 border-primary/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <UserCheck className="w-8 h-8 text-primary" />
                    <div>
                      <p className="text-sm text-muted-foreground">Active Subscribers</p>
                      <p className="text-2xl font-bold text-primary">
                        {users.filter(u => u.subscription_status === 'active').length}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payments Journal */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5" />
                  Payment Journal ({filteredTransactions.length} transactions)
                  {(paymentNameFilter || paymentDateFrom || paymentDateTo) && (
                    <Badge variant="secondary" className="ml-2">Filtered</Badge>
                  )}
                </CardTitle>
                <CardDescription>Complete history of all payment transactions, ordered by most recent</CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Stripe Reference</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedPayments.map(tx => (
                      <TableRow key={tx.id} data-testid={`payment-row-${tx.id}`}>
                        <TableCell className="whitespace-nowrap">
                          <div>
                            <p className="font-medium">{formatDate(tx.created_at)}</p>
                            {tx.paid_at && (
                              <p className="text-xs text-muted-foreground">Paid: {formatDate(tx.paid_at)}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">{tx.user_name || 'Unknown'}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm">{tx.user_email}</p>
                        </TableCell>
                        <TableCell>
                          <p className="font-bold text-green-500">${tx.amount?.toFixed(2) || '0.00'}</p>
                          <p className="text-xs text-muted-foreground uppercase">{tx.currency || 'usd'}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {tx.plan_type || 'monthly'}
                          </Badge>
                          {tx.auto_renew && (
                            <Badge variant="secondary" className="ml-1 text-xs">Auto</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={tx.payment_status === 'paid' ? 'default' : tx.payment_status === 'pending' ? 'secondary' : 'destructive'}>
                            {tx.payment_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted/50 px-2 py-1 rounded font-mono">
                            {tx.session_id?.slice(0, 20)}...
                          </code>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => window.open(`https://dashboard.stripe.com/payments?query=${encodeURIComponent(tx.session_id)}`, '_blank')}
                              title="View in Stripe Dashboard"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const user = users.find(u => u.id === tx.user_id);
                                if (user) {
                                  setEditingUser(user);
                                  setActiveSection('users');
                                }
                              }}
                              title="View User Details"
                            >
                              <Users className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredTransactions.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          {(paymentNameFilter || paymentDateFrom || paymentDateTo) 
                            ? "No transactions match your filters" 
                            : "No payment transactions yet"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {filteredTransactions.length > 0 && (
                  <PaginationControls
                    currentPage={paymentsPage}
                    totalPages={getTotalPages(filteredTransactions)}
                    onPageChange={setPaymentsPage}
                    totalItems={filteredTransactions.length}
                    itemName="transactions"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Section */}
        {activeSection === "users" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    All Users ({filteredUsers.length})
                  </CardTitle>
                  <div className="relative w-72">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={userSearch}
                      onChange={(e) => handleUserSearchChange(e.target.value)}
                      className="pl-9"
                      data-testid="user-search-input"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Credits</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedUsers.map(user => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {user.email}
                            {user.is_admin && (
                              <Badge variant="destructive" className="text-xs">Admin</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{user.name}</TableCell>
                        <TableCell>
                          <span className="font-mono font-bold" data-testid={`user-credits-${user.id}`}>{user.credits ?? 0}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {user.last_login ? new Date(user.last_login).toLocaleString() : "Never"}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => {
                                setPaymentNameFilter(user.email);
                                setActiveSection("payments");
                              }}
                              title="View payments"
                              data-testid={`view-payments-${user.id}`}
                            >
                              <DollarSign className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => setEditingUser(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              Edit
                            </Button>
                            {!user.is_admin && (
                              <AlertDialog data-testid={`delete-user-dialog-${user.id}`}>
                                <AlertDialogTrigger asChild>
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-destructive hover:text-destructive"
                                    data-testid={`delete-user-${user.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent data-testid={`delete-user-dialog-content-${user.id}`}>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle data-testid={`delete-user-dialog-title-${user.id}`}>Delete User</AlertDialogTitle>
                                    <AlertDialogDescription data-testid={`delete-user-dialog-description-${user.id}`}>
                                      Are you sure you want to delete user {user.email}? This will also delete all their connections, queries, and history. This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel data-testid={`delete-user-cancel-${user.id}`}>Cancel</AlertDialogCancel>
                                    <AlertDialogAction onClick={() => handleDeleteUser(user.id, user.email)} className="bg-destructive hover:bg-destructive/90" data-testid={`delete-user-confirm-${user.id}`}>
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredUsers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          {userSearch ? `No users found matching "${userSearch}"` : "No users yet"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
                {filteredUsers.length > 0 && (
                  <PaginationControls
                    currentPage={usersPage}
                    totalPages={getTotalPages(filteredUsers)}
                    onPageChange={setUsersPage}
                    totalItems={filteredUsers.length}
                    itemName="users"
                  />
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Renewals Section */}
        {activeSection === "renewals" && (
          <div className="space-y-6">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or email..."
                      value={renewalNameFilter}
                      onChange={(e) => handleRenewalFilterChange(setRenewalNameFilter)(e.target.value)}
                      className="pl-9"
                      data-testid="renewal-name-filter"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Expires:</span>
                    <Input
                      type="date"
                      value={renewalDateFrom}
                      onChange={(e) => handleRenewalFilterChange(setRenewalDateFrom)(e.target.value)}
                      className="w-40"
                      data-testid="renewal-date-from"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="date"
                      value={renewalDateTo}
                      onChange={(e) => handleRenewalFilterChange(setRenewalDateTo)(e.target.value)}
                      className="w-40"
                      data-testid="renewal-date-to"
                    />
                  </div>
                  {(renewalNameFilter || renewalDateFrom || renewalDateTo) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setRenewalNameFilter("");
                        setRenewalDateFrom("");
                        setRenewalDateTo("");
                        setRenewalsPage(1);
                      }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-yellow-500">
                  <AlertCircle className="w-5 h-5" />
                  Users Due for Renewal ({filteredRenewals.length})
                  {(renewalNameFilter || renewalDateFrom || renewalDateTo) && (
                    <Badge variant="secondary" className="ml-2">Filtered</Badge>
                  )}
                </CardTitle>
                <CardDescription>
                  Users whose subscription expires within 7 days
                </CardDescription>
              </CardHeader>
              <CardContent>
                {filteredRenewals.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <AlertCircle className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>{(renewalNameFilter || renewalDateFrom || renewalDateTo) 
                      ? "No renewals match your filters" 
                      : "No users due for renewal in the next 7 days"}</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Name</TableHead>
                          <TableHead>Expires</TableHead>
                          <TableHead>Days Left</TableHead>
                          <TableHead>Auto Renew</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedRenewals.map(user => (
                          <TableRow key={user.id}>
                            <TableCell>{user.email}</TableCell>
                            <TableCell>{user.name}</TableCell>
                            <TableCell>{formatDate(user.subscription_expires)}</TableCell>
                            <TableCell>
                              <Badge variant={user.days_until_renewal <= 3 ? 'destructive' : 'secondary'}>
                                {user.days_until_renewal} days
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge variant={user.auto_renew ? "default" : "secondary"}>
                                {user.auto_renew ? "Yes" : "No"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="gap-2"
                                onClick={() => window.open(`https://dashboard.stripe.com/customers?email=${encodeURIComponent(user.email)}`, '_blank')}
                              >
                                <ExternalLink className="w-3 h-3" />
                                Stripe
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {filteredRenewals.length > 0 && (
                      <PaginationControls
                        currentPage={renewalsPage}
                        totalPages={getTotalPages(filteredRenewals)}
                        onPageChange={setRenewalsPage}
                        totalItems={filteredRenewals.length}
                        itemName="renewals"
                      />
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Promotions Section */}
        {activeSection === "promotions" && (
          <PromotionsSection API_URL={API_URL} getAuthHeaders={getAuthHeaders} siteName={settings.site_name || "AIQueryStudio"} />
        )}

        {/* Bot Insights Section */}
        {activeSection === "bot" && (
          <BotInsightsSection API_URL={API_URL} getAuthHeaders={getAuthHeaders} />
        )}

        {/* Security Section */}
        {activeSection === "security" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Change Admin Password</CardTitle>
                <CardDescription>Update your admin login credentials. You must provide your current password.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Current Password</Label>
                  <Input
                    type="password"
                    value={passwordData.current}
                    onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                    placeholder="Enter current password"
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.new}
                    onChange={(e) => setPasswordData({ ...passwordData, new: e.target.value })}
                    placeholder="Enter new password (min 8 characters)"
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Confirm New Password</Label>
                  <Input
                    type="password"
                    value={passwordData.confirm}
                    onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button onClick={handleUpdatePassword} data-testid="update-password-btn">
                  Update Password
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* Edit User Dialog */}
      <Dialog open={!!editingUser} onOpenChange={() => setEditingUser(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>{editingUser?.email}</DialogDescription>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Credits</Label>
                <Input
                  type="number"
                  min="0"
                  value={editingUser.credits ?? 0}
                  onChange={(e) => setEditingUser({ ...editingUser, credits: parseInt(e.target.value) || 0 })}
                  data-testid="edit-user-credits"
                />
                <p className="text-xs text-muted-foreground">Current credit balance for this user</p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Bypass Paywall</Label>
                <Switch
                  checked={editingUser.bypass_paywall || false}
                  onCheckedChange={(checked) => setEditingUser({ ...editingUser, bypass_paywall: checked })}
                />
              </div>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
                <Button onClick={() => handleUpdateUserSubscription(editingUser.id, {
                  credits: editingUser.credits,
                  bypass_paywall: editingUser.bypass_paywall
                })} data-testid="save-user-changes-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
